// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: ClickUp task links pasted in chats open the task inside Konvo instead of a browser tab.

import KonvoWorkspace from '@app/features/konvo_tasks/state/KonvoWorkspace';

/**
 * The task id in a ClickUp task link: `https://app.clickup.com/t/86c2q4r` or, for custom ids,
 * `https://app.clickup.com/t/9012345/ICAM-123`. Same shapes the server unfurls
 * (KonvoClickUpUnfurlerService.parseClickUpTaskUrl).
 */
export function clickUpTaskIdFromUrl(value: string | null | undefined): string | null {
	if (!value) return null;
	let url: URL;
	try {
		url = new URL(value);
	} catch {
		return null;
	}
	if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== 'app.clickup.com') return null;
	const segments = url.pathname.split('/').filter(Boolean);
	if (segments[0]?.toLowerCase() !== 't' || (segments.length !== 2 && segments.length !== 3)) return null;
	if (segments.length === 3 && !/^\d+$/.test(segments[1]!)) return null;
	let taskId: string;
	try {
		taskId = decodeURIComponent(segments.at(-1)!).trim();
	} catch {
		return null;
	}
	return /^[a-zA-Z0-9_-]{2,128}$/.test(taskId) ? taskId : null;
}

/** A plain left click; modifier and middle clicks keep opening ClickUp itself. */
export function isPlainClick(event: Pick<MouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>) {
	return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * Opens a ClickUp task link as Konvo's task view. False when the link is not a task or ClickUp
 * is not connected, so the caller opens it in the browser as before.
 */
export function openClickUpTaskLink(url: string): boolean {
	const taskId = clickUpTaskIdFromUrl(url);
	if (!taskId || KonvoWorkspace.connected === false) return false;
	// Lazy: the task modal is heavy and chats render many links.
	void import('@app/features/konvo_tasks/commands/KonvoTaskCommands').then(({openKonvoTask}) =>
		openKonvoTask(taskId, 'overview', url),
	);
	return true;
}
